'use client'

import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { QrCode, X, Loader2 } from 'lucide-react'
import { useParty } from '@/lib/party-context'

/**
 * Bouton QR CODE (à côté du logo BLC) : un clic affiche EN GRAND, plein écran,
 * le QR code pour rejoindre la BAFFLE (soirée). Si aucune soirée n'est en
 * cours, elle est créée (cet appareil devient la baffle).
 * Fermeture : bouton ✕ (focalisé d'office, OK de la télécommande), Retour/Échap
 * ou clic sur le fond.
 */
export default function PartyQrButton({ className = '', size = 20 }: { className?: string; size?: number }) {
  const { role, code, becomeHost } = useParty()
  const [open, setOpen] = useState(false)
  const [qr, setQr] = useState<string | null>(null)
  const [url, setUrl] = useState('')
  const [roomCode, setRoomCode] = useState('')
  const [err, setErr] = useState('')
  const closeRef = useRef<HTMLButtonElement>(null)

  const show = async () => {
    setOpen(true); setErr('')
    try {
      const reuse = role === 'host' && code ? code : ''
      const r = await fetch('/api/party/create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reuse ? { hostName: 'Soirée', code: reuse } : { hostName: 'Soirée' }),
      })
      const j = await r.json()
      if (!j?.ok) throw new Error(j?.error || 'échec')
      if (!reuse) becomeHost(j.code, 'Soirée')
      setQr(j.qr || null); setUrl(j.joinUrl || ''); setRoomCode(j.code || '')
    } catch (e: any) { setErr('Impossible de créer la soirée. ' + (e?.message || '')) }
  }

  useEffect(() => {
    if (!open) return
    const t = setTimeout(() => { try { closeRef.current?.focus() } catch {} }, 60)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Backspace' || e.key === 'GoBack' || e.keyCode === 4) {
        e.preventDefault(); e.stopImmediatePropagation(); setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey, true)
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey, true) }
  }, [open])

  return (
    <>
      <button type="button" onClick={show} title="QR code de la baffle (soirée)" aria-label="Afficher le QR code de la baffle"
        className={`blc-qr-btn lglass shrink-0 grid place-items-center rounded-2xl text-white ${className}`}>
        <QrCode size={size} />
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <div className="blc-qr-full fixed inset-0 z-[2147483500] grid place-items-center bg-[rgb(var(--sbg)/.92)] backdrop-blur-xl p-[4vmin]"
          onClick={() => setOpen(false)}>
          <div className="lg-surface relative rounded-[4vmin] p-[4vmin] text-center text-white max-w-[92vmin]" onClick={e => e.stopPropagation()}>
            <button ref={closeRef} type="button" onClick={() => setOpen(false)} aria-label="Fermer"
              className="lglass absolute top-[2vmin] right-[2vmin] w-[7vmin] h-[7vmin] min-w-11 min-h-11 rounded-full grid place-items-center">
              <X style={{ width: '50%', height: '50%' }} />
            </button>
            <div className="text-[2.2vmin] font-semibold tracking-[0.3em] text-white/60">BAFFLE · SOIRÉE</div>
            <div className="text-[4.2vmin] font-extrabold mt-[1vmin] mb-[3vmin] leading-tight">Scanne pour rejoindre la soirée</div>
            <div className="mx-auto bg-white rounded-[3vmin] p-[2.4vmin] w-[min(70vmin,560px)] aspect-square grid place-items-center shadow-[0_0_60px_rgb(var(--a2)/.55)]">
              {qr ? <img src={qr} alt="QR code pour rejoindre la baffle" className="w-full h-full" style={{ imageRendering: 'pixelated' }} />
                : err ? <span className="text-[#0a0418] text-base px-4">{err}</span>
                : <Loader2 className="animate-spin text-[#0a0418]" size={48} />}
            </div>
            {roomCode && <div className="mt-[3vmin] text-[3.4vmin] font-bold">Code : <span className="text-a3 tracking-[0.2em]">{roomCode}</span></div>}
            {url && <div className="mt-[1vmin] text-[2vmin] text-white/60 break-all">{url}</div>}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}
